/**
 * Le permis de conduire vaut-il pièce d'identité ? (référentiel CTVO §6, §16-ter)
 *
 * - Format carte (post-2013) : OUI, pour tout le monde. Quand il est au
 *   dossier, on ne réclame pas en plus une CNI.
 * - Format papier rose (pré-2013) : NON, la CNI ou le passeport reste exigé.
 * - ADCS : NON, elle n'a pas de photo (préfecture, 16/09/2026).
 *
 * Fonction pure, partagée entre la règle cross-check
 * `coherence_acquereur_cession_vs_pi`, la checklist agence (FormCGIAv2), le
 * calcul des pièces manquantes à la soumission (RecapAgencyScreen) et la grille
 * de review (PiecesGrid). Sans ce partage, la règle acceptait le permis carte
 * mais les trois autres écrans réclamaient quand même une « pièce d'identité »
 * et ouvraient une demande impossible à satisfaire (GZ-923-ZV, retour François
 * du 02/10/2026).
 */

/** Le permis format carte bancaire est délivré depuis le 19/01/2013. */
export const PERMIS_FORMAT_CARTE_DEPUIS = new Date('2013-01-19T00:00:00Z');

/**
 * Warnings OCR qui signalent une ADCS (attestation de droits à conduire
 * sécurisée). `permis_adcs` est le warning du prompt `permis_conduire` ; l'autre
 * est le libellé que le modèle produisait de lui-même avant (extractions
 * EE-061-SB et GF-921-VW, août 2026).
 */
export const ADCS_WARNINGS = ['permis_adcs', 'attestation_droits_conduire_rir_pas_permis_physique'];

export interface PermisLu {
  dateExpiration: unknown;
  dateDelivrance: unknown;
  warnings: unknown;
}

export const estAdcsFromWarnings = (warnings: unknown): boolean =>
  Array.isArray(warnings) && warnings.some((w) => typeof w === 'string' && ADCS_WARNINGS.includes(w));

export const permisLuVautPieceIdentite = ({ dateExpiration, dateDelivrance, warnings }: PermisLu): boolean => {
  if (estAdcsFromWarnings(warnings)) return false;
  // Une date d'expiration lue n'existe que sur le format carte.
  if (typeof dateExpiration === 'string' && dateExpiration) return true;

  // Le rose ne s'écarte que s'il est POSITIVEMENT identifié par sa date de
  // délivrance : une date non lue est une lecture ratée, pas un permis rose
  // (règle maîtresse « donnée mal lue ≠ donnée manquante »).
  const delivranceDate = typeof dateDelivrance === 'string' && dateDelivrance ? new Date(dateDelivrance) : null;
  const estAncienFormatRose =
    delivranceDate !== null && !Number.isNaN(delivranceDate.getTime()) && delivranceDate < PERMIS_FORMAT_CARTE_DEPUIS;

  return !estAncienFormatRose;
};

/** Champs OCR tels que stockés sur une pièce (store agence ou extraction review). */
type ChampsOcr = Record<string, { value?: unknown } | undefined> | undefined;

/**
 * Variante pour une pièce déposée : prend les champs OCR et les warnings du
 * permis. `undefined` (pas de permis au dossier) → faux.
 */
export const permisDeposeVautPieceIdentite = (
  permis: { fields: ChampsOcr; warnings: unknown } | undefined,
): boolean => {
  if (!permis) return false;
  return permisLuVautPieceIdentite({
    dateExpiration: permis.fields?.date_expiration?.value,
    dateDelivrance: permis.fields?.date_delivrance?.value,
    warnings: permis.warnings,
  });
};
